import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Rocket,
  FileSearch,
  Download,
  MessageCircle,
  ChevronLeft,
  Heart,
  Tag,
  Shield,
  BookOpen,
  Mail,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const AboutUsPage = () => {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Sortio',
      url: 'https://www.getsortio.com/about-us',
      mainEntity: {
        '@type': 'Organization',
        name: 'Sortio',
        legalName: 'CMG Labs, LLC',
        url: 'https://www.getsortio.com',
        logo: 'https://www.getsortio.com/logo.png',
        email: 'marcus@getsortio.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '3790 El Camino Real, Unit #593',
          addressLocality: 'Palo Alto',
          addressRegion: 'CA',
          postalCode: '94306',
          addressCountry: 'US',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'marcus@getsortio.com',
          contactType: 'customer support',
        },
        sameAs: [
          'https://github.com/canonicalmg/Sortio-Releases',
          'https://apps.apple.com/app/id6737292062',
        ],
      },
    },
  ];

  const resourceCards = [
    {
      title: 'Changelog',
      description: 'What we shipped, by version and date. Updated with every release.',
      href: '/changelog',
      external: false,
      icon: <Tag className="w-6 h-6" />,
      color: 'from-sortio-primary/10 to-sortio-primary/5',
      iconColor: 'text-sortio-primary',
    },
    {
      title: 'Security',
      description: 'What data goes where, hosted-AI retention, and the options for keeping content local.',
      href: '/security',
      external: false,
      icon: <Shield className="w-6 h-6" />,
      color: 'from-sortio-secondary/10 to-sortio-secondary/5',
      iconColor: 'text-sortio-secondary',
    },
    {
      title: 'Docs',
      description: 'Setup guides, automations, local models, and integrations.',
      href: '/docs',
      external: false,
      icon: <BookOpen className="w-6 h-6" />,
      color: 'from-sortio-accent/10 to-sortio-accent/5',
      iconColor: 'text-sortio-accent',
    },
    {
      title: 'Contact',
      description: 'marcus@getsortio.com. A human reads and answers every message.',
      href: 'mailto:marcus@getsortio.com',
      external: true,
      icon: <Mail className="w-6 h-6" />,
      color: 'from-blue-500/10 to-blue-500/5',
      iconColor: 'text-blue-500',
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <SEOHead
        title="About Sortio: Who Builds It and How to Reach Us"
        description="Sortio is an AI file organizer for Mac, Windows, and Linux, built by CMG Labs, LLC, a small independent team in Palo Alto, California. What the app does, who makes it, and how to get in touch."
        canonical="https://www.getsortio.com/about-us"
        schema={schema}
      />

      {/* Background gradient */}
      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>
      <div className="absolute -top-[600px] -right-[300px] w-[1000px] h-[1000px] bg-sortio-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-[600px] -left-[300px] w-[1000px] h-[1000px] bg-sortio-secondary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        {/* Header */}
        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Users className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            About <span className="apple-gradient-text">Sortio</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-xl">
            The AI file organizer for Mac, Windows, and Linux. Built and run by a small independent team.
          </p>
        </div>

        {/* Content */}
        <div className="space-y-16">
          {/* What Sortio Is */}
          <section className="relative">
            <div className="absolute -right-16 top-0 w-32 h-32 rounded-full bg-sortio-primary/10 blur-3xl opacity-50"></div>
            <div className="apple-panel p-8 rounded-apple backdrop-blur-sm relative overflow-hidden">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-14 h-14 rounded-full bg-sortio-primary/20 flex items-center justify-center flex-shrink-0 mb-4 md:mb-0">
                  <FileSearch className="w-6 h-6 text-sortio-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-apple-50 mb-4">What Sortio Is</h2>
                  <p className="text-apple-300 leading-relaxed mb-4">
                    Sortio is a desktop app that organizes files from a plain-language prompt. Drop a folder,
                    describe how you want it organized, and Sortio renames every file and files it where it
                    belongs. Every sort shows a preview before anything moves, and every run can be undone.
                  </p>
                  <p className="text-apple-300 leading-relaxed">
                    Beyond one-off sorts, automations watch folders and keep them organized as new files
                    arrive, and built-in semantic search and chat help you find what you already have. The
                    app ships in six languages: English, Chinese (Simplified and Traditional), Korean,
                    German, and Spanish.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Who Builds It */}
          <section className="relative">
            <div className="absolute -left-16 top-0 w-32 h-32 rounded-full bg-sortio-secondary/10 blur-3xl opacity-50"></div>
            <div className="apple-panel p-8 rounded-apple backdrop-blur-sm relative overflow-hidden">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-14 h-14 rounded-full bg-sortio-secondary/20 flex items-center justify-center flex-shrink-0 mb-4 md:mb-0">
                  <Heart className="w-6 h-6 text-sortio-secondary" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-apple-50 mb-4">Who Builds It</h2>
                  <p className="text-apple-300 leading-relaxed mb-4">
                    Sortio is built by CMG Labs, LLC, a US company based in Palo Alto, California. We are a
                    small independent team: the same people who write the code answer the support email.
                    Marcus, Sortio's founder, reads every message sent to{' '}
                    <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80 transition-colors">
                      marcus@getsortio.com
                    </a>.
                  </p>
                  <p className="text-apple-300 leading-relaxed">
                    Sortio started the way most file organizers probably do: a Downloads folder that had
                    gotten completely out of hand, and the realization that modern AI could finally read a
                    file and know where it belongs. We built the tool we wanted to use, then kept going.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How We Work */}
          <section className="relative">
            <div className="absolute -right-16 top-0 w-32 h-32 rounded-full bg-sortio-accent/10 blur-3xl opacity-50"></div>
            <div className="apple-panel p-8 rounded-apple backdrop-blur-sm relative overflow-hidden">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-14 h-14 rounded-full bg-sortio-accent/20 flex items-center justify-center flex-shrink-0 mb-4 md:mb-0">
                  <Rocket className="w-6 h-6 text-sortio-accent" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-apple-50 mb-4">How We Work</h2>
                  <p className="text-apple-300 leading-relaxed mb-4">
                    We ship frequently and document it. Every release is listed in the{' '}
                    <Link to="/changelog" className="text-sortio-primary hover:text-sortio-primary/80 transition-colors">changelog</Link>,
                    with full notes on{' '}
                    <a
                      href="https://github.com/canonicalmg/Sortio-Releases/releases"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sortio-primary hover:text-sortio-primary/80 transition-colors"
                    >
                      GitHub
                    </a>. File safety comes first: Sortio previews every move, never overwrites a different
                    file that shares a name, and refuses to touch system folders.
                  </p>
                  <p className="text-apple-300 leading-relaxed">
                    On privacy, we say exactly what happens: by default the AI runs on our cloud, and content
                    sent for hosted AI processing is purged within 30 days. If you want file contents to stay
                    off our servers, bring your own OpenAI or Anthropic key, or run fully local with Ollama
                    or LM Studio. The{' '}
                    <Link to="/security" className="text-sortio-primary hover:text-sortio-primary/80 transition-colors">security page</Link>{' '}
                    spells out what is sent where.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Resources */}
          <section>
            <div className="text-center mb-12">
              <h2 className="text-2xl font-semibold text-apple-50 mb-2">Find What You Need</h2>
              <p className="text-apple-300 max-w-lg mx-auto">The pages people usually come here looking for.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {resourceCards.map((card, index) => {
                const inner = (
                  <>
                    <div className={`w-12 h-12 rounded-full bg-apple-700/40 flex items-center justify-center mb-4 ${card.iconColor}`}>
                      {card.icon}
                    </div>
                    <h3 className="text-xl font-medium text-apple-50 mb-2">{card.title}</h3>
                    <p className="text-apple-300">{card.description}</p>
                  </>
                );
                const cardClass = `block p-6 rounded-apple bg-gradient-to-br ${card.color} border border-apple-600/30 backdrop-blur-sm hover:border-apple-500/50 transition-colors`;
                return card.external ? (
                  <a key={index} href={card.href} className={cardClass}>
                    {inner}
                  </a>
                ) : (
                  <Link key={index} to={card.href} className={cardClass}>
                    {inner}
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Try It */}
          <section className="apple-panel p-8 rounded-apple backdrop-blur-sm relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-sortio-primary/20 to-sortio-secondary/20 rounded-full blur-3xl"></div>

            <div className="relative">
              <h2 className="text-2xl font-semibold text-apple-50 mb-4">Try It Yourself</h2>
              <p className="text-apple-300 leading-relaxed mb-8">
                The download is free, and the free tier includes a one-time AI trial allowance with no card required.
                Point it at your messiest folder and watch the preview. If something is unclear or broken,
                tell us, because that is how most of the changelog gets written.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/download" className="apple-button inline-flex items-center justify-center">
                  <Download className="w-5 h-5 mr-2" />
                  <span>Download Sortio</span>
                </Link>
                <Link to="/#contact" className="inline-flex items-center justify-center px-5 py-3 rounded-full border border-apple-600/40 bg-apple-700/40 text-apple-100 hover:bg-apple-600/50 transition-all">
                  <MessageCircle className="w-5 h-5 mr-2 text-sortio-primary" />
                  <span>Contact Us</span>
                </Link>
              </div>
            </div>
          </section>
        </div>

        {/* Back to top button */}
        <div className="flex justify-center mt-16 mb-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-4 py-2 rounded-full bg-apple-700/40 border border-apple-600/30 text-sm text-apple-300 hover:text-sortio-primary transition-colors"
          >
            <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m18 15-6-6-6 6"/>
            </svg>
            <span>Back to top</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;
